/**
 * wall-art-picker.js  (darphane-lite / "Oda Galeri")
 *
 * Custom element <wall-art-picker> driving the Format / Boyut / Çerçeve picker
 * over NATIVE Shopify variants. The lite shop sells real variants with native
 * prices — there is NO pricing_variant_map / shadow product here (that was the
 * main shop). This element therefore reads the product's own variants and
 * resolves the matching native variant id.
 *
 * Format + Boyut are native <select> DROPDOWNS; Çerçeve stays a swatch row.
 *
 * Reads (embedded JSON inside the element by wall-art-picker.liquid):
 *   - [data-wap-variants]      → `product.variants | json`
 *       each: { id, title, price (cents), available, option1, option2, option3, ... }
 *       option1 = Format label, option2 = Boyut label, option3 = Çerçeve label
 *   - [data-wap-option-names]  → { format, size, frame } option display names
 *
 * Writes:
 *   - The product form's hidden <input name="id"> → resolved native variant id
 *     (so /cart/add submits the right variant; Shopify carries Format/Boyut/
 *     Çerçeve on the line item natively — no _design_* line properties needed).
 *   - Displayed price: handled by wall-art-price-atc.liquid + wall-art-sale-
 *     pricing.js, both of which listen to the event below. We also overwrite any
 *     standalone .price elements as a fallback.
 *
 * Emits:
 *   - 'wallart:selection:changed' with the KEYS the gallery/video consume:
 *       { formatKey, frameKey, sizeKey, variantId, price }
 *     `price` is in MAJOR units (TRY), i.e. cents/100, matching the contract
 *     wall-art-price-atc.liquid expects (it multiplies detail.price * 100).
 *
 * Label→key maps (must match config/lite-catalog.json + oda.gallery keys):
 *   Format  "Art Print"→art_print, "Çerçeveli Tablo"→cerceveli_tablo
 *   Çerçeve "Yok"→yok, "Siyah"→siyah, "Beyaz"→beyaz, "Ahşap"→ahsap
 *   Boyut   "<WxH> cm"→"<WxH>"  (the " cm" suffix stripped)
 */

if (!customElements.get('wall-art-picker')) {
  // ── Fixed label→key maps (locked lite catalog) ─────────────────────────────
  const FORMAT_LABEL_TO_KEY = {
    'Art Print': 'art_print',
    'Çerçeveli Tablo': 'cerceveli_tablo',
  };
  const FRAME_LABEL_TO_KEY = {
    Yok: 'yok',
    Siyah: 'siyah',
    Beyaz: 'beyaz',
    Ahşap: 'ahsap',
  };

  /** Normalize a Turkish string for tolerant map lookup (handles stray casing
   *  / whitespace; the maps above use the canonical catalog labels). */
  function norm(s) {
    return String(s == null ? '' : s).trim();
  }

  function formatKeyFor(label) {
    const l = norm(label);
    if (FORMAT_LABEL_TO_KEY[l]) return FORMAT_LABEL_TO_KEY[l];
    // Fallback: slugify (locale-independent) so an unexpected label still keys.
    return l.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  function frameKeyFor(label) {
    const l = norm(label);
    if (FRAME_LABEL_TO_KEY[l]) return FRAME_LABEL_TO_KEY[l];
    return l.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  }

  /** Size key: drop the trailing " cm" unit. "21x30 cm" → "21x30". */
  function sizeKeyFor(label) {
    return norm(label).replace(/\s*cm\s*$/i, '').trim();
  }

  /** A format is "framed" iff it has any frame value other than "Yok". */
  function formatIsFramed(frameLabelsForFormat) {
    return frameLabelsForFormat.some((f) => frameKeyFor(f) !== 'yok');
  }

  class WallArtPicker extends HTMLElement {
    connectedCallback() {
      this.variants = this.parseJson('[data-wap-variants]') || [];
      this.optionNames = this.parseJson('[data-wap-option-names]') || {};
      if (!Array.isArray(this.variants) || this.variants.length === 0) return;

      this.formId = this.dataset.productFormId;
      this.form = this.formId ? document.getElementById(this.formId) : null;
      if (!this.form) console.warn('[wall-art-picker] product form not found:', this.formId);

      // Format + Boyut are <select> dropdowns; Çerçeve is a swatch row.
      this.formatSelect = this.querySelector('[data-wap-format-select]');
      this.sizeSelect = this.querySelector('[data-wap-size-select]');
      this.framePicker = this.querySelector('[data-wap-frame-picker]');
      this.frameSwatchesHost = this.querySelector('[data-wap-frame-swatches]');
      this.frameSelectedLabel = this.querySelector('[data-wap-frame-selected-label]');

      // Current selection held as native option LABELS (what variants match on).
      this.state = { format: null, size: null, frame: null };

      this.buildIndex();
      this.bindEvents();

      // Initial selection: prefer the product's selected/first available variant
      // (Shopify already picks a sensible default), falling back to the first
      // format in DOM order.
      const initial = this.pickInitialVariant();
      if (initial) {
        this.selectFormat(initial.option1, {
          preferSize: initial.option2,
          preferFrame: initial.option3,
        });
      } else if (this.formatLabels.length) {
        this.selectFormat(this.formatLabels[0]);
      }
    }

    parseJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent.trim());
      } catch (err) {
        console.error('[wall-art-picker] failed to parse', selector, err);
        return null;
      }
    }

    /** Pick the variant to land on: the one Shopify marked selected via the
     *  ?variant= param if present, else the first available, else the first. */
    pickInitialVariant() {
      let variantId = null;
      try {
        const u = new URL(window.location.href);
        const v = u.searchParams.get('variant');
        if (v) variantId = String(v);
      } catch { /* ignore */ }
      if (variantId) {
        const m = this.variants.find((v) => String(v.id) === variantId);
        if (m) return m;
      }
      return this.variants.find((v) => v.available) || this.variants[0] || null;
    }

    /**
     * Build lookup structures over the native variants:
     *   formatLabels:   ordered unique Format labels (from the option, DOM order)
     *   sizesByFormat:  { formatLabel -> ordered unique size labels (available-aware) }
     *   framesByFormat: { formatLabel -> ordered unique frame labels }
     *   variantByCombo: { "fmt||size||frame" -> variant }
     */
    buildIndex() {
      this.variantByCombo = {};
      this.sizesByFormat = {};
      this.framesByFormat = {};
      const formatOrder = [];

      // Preserve option-declared order where possible (the <option>s follow
      // product.options_with_values order).
      const fmtOptions = this.formatSelect ? Array.from(this.formatSelect.options) : [];
      for (const o of fmtOptions) formatOrder.push(norm(o.value));

      for (const v of this.variants) {
        const fmt = norm(v.option1);
        const size = norm(v.option2);
        const frame = norm(v.option3);
        if (!fmt || !size) continue;
        this.variantByCombo[this.comboKey(fmt, size, frame)] = v;

        if (!formatOrder.includes(fmt)) formatOrder.push(fmt);
        (this.sizesByFormat[fmt] ??= []);
        if (!this.sizesByFormat[fmt].includes(size)) this.sizesByFormat[fmt].push(size);
        (this.framesByFormat[fmt] ??= []);
        if (frame && !this.framesByFormat[fmt].includes(frame)) this.framesByFormat[fmt].push(frame);
      }
      this.formatLabels = formatOrder.filter((f, i) => formatOrder.indexOf(f) === i);

      // Mark format options that have NO variant at all as disabled (defensive).
      for (const o of fmtOptions) {
        const hasAny = !!this.sizesByFormat[norm(o.value)];
        o.disabled = !hasAny;
      }
    }

    comboKey(fmt, size, frame) {
      return `${norm(fmt)}||${norm(size)}||${norm(frame)}`;
    }

    /** Is there an AVAILABLE variant for this combo? (purchasable) */
    isComboAvailable(fmt, size, frame) {
      const v = this.variantByCombo[this.comboKey(fmt, size, frame)];
      return !!(v && v.available);
    }

    /** Does a variant exist (regardless of availability)? */
    comboExists(fmt, size, frame) {
      return !!this.variantByCombo[this.comboKey(fmt, size, frame)];
    }

    bindEvents() {
      if (this.formatSelect) {
        this.formatSelect.addEventListener('change', () => {
          this.selectFormat(this.formatSelect.value);
        });
      }
      if (this.sizeSelect) {
        this.sizeSelect.addEventListener('change', () => {
          this.selectSize(this.sizeSelect.value);
        });
      }
      if (this.frameSwatchesHost) {
        this.frameSwatchesHost.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-wap-frame-label]');
          if (!btn || btn.disabled) return;
          this.selectFrame(btn.dataset.wapFrameLabel);
        });
      }
    }

    // ─── Selection ──────────────────────────────────────────────────────────

    selectFormat(format, opts = {}) {
      format = norm(format);
      if (!this.sizesByFormat[format]) return;

      const prevSize = this.state.size;
      const prevFrame = this.state.frame;
      this.state.format = format;
      this.state.size = null;
      this.state.frame = null;

      // Reflect on the format dropdown (no-op if a change event already set it).
      if (this.formatSelect && this.formatSelect.value !== format) {
        this.formatSelect.value = format;
      }

      // Frames for this format. Hide the group entirely for a non-framed format
      // (Art Print → only "Yok").
      const frameLabels = this.framesByFormat[format] || [];
      const framed = formatIsFramed(frameLabels);
      if (this.framePicker) this.framePicker.hidden = !framed;

      // Resolve the frame label to keep first (size availability depends on it):
      //   1. caller-preferred (initial variant)  2. prior selection if offered
      //   3. first framed value                  4. the implicit "Yok"
      let frame;
      if (framed) {
        if (opts.preferFrame && frameLabels.includes(norm(opts.preferFrame))) {
          frame = norm(opts.preferFrame);
        } else if (prevFrame && frameLabels.includes(prevFrame)) {
          frame = prevFrame;
        } else {
          frame = frameLabels.find((f) => frameKeyFor(f) !== 'yok') || frameLabels[0];
        }
      } else {
        // Non-framed: use the "Yok" value the variants actually carry.
        frame = frameLabels.find((f) => frameKeyFor(f) === 'yok') || frameLabels[0] || 'Yok';
      }
      this.state.frame = frame;

      if (framed) this.renderFrameSwatches(frameLabels);

      // Sizes for this format.
      const sizes = this.sizesByFormat[format] || [];
      this.renderSizeOptions(sizes);
      this.refreshSizeAvailability();

      // Resolve the size to keep:
      //   1. caller-preferred  2. prior selection if still purchasable
      //   3. first available   4. first existing
      let size = null;
      const preferSize = opts.preferSize ? norm(opts.preferSize) : null;
      if (preferSize && sizes.includes(preferSize) && this.comboExists(format, preferSize, frame)) {
        size = preferSize;
      } else if (prevSize && sizes.includes(prevSize) && this.isComboAvailable(format, prevSize, frame)) {
        size = prevSize;
      } else {
        size = sizes.find((s) => this.isComboAvailable(format, s, frame))
          || sizes.find((s) => this.comboExists(format, s, frame))
          || sizes[0];
      }

      if (size) {
        this.state.size = size;
        this.markSelectedSize(size);
      }
      this.refreshFrameAvailability();
      this.commit();
    }

    selectSize(size) {
      size = norm(size);
      if (!this.state.format) return;
      if (!this.comboExists(this.state.format, size, this.state.frame)) {
        // Chosen size unavailable in the current frame — snap the frame to one
        // that offers it (keeps the customer's size intent).
        const frames = this.framesByFormat[this.state.format] || [];
        const altFrame = frames.find((f) => this.isComboAvailable(this.state.format, size, f))
          || frames.find((f) => this.comboExists(this.state.format, size, f));
        if (altFrame) this.state.frame = altFrame;
      }
      this.state.size = size;
      this.markSelectedSize(size);
      this.refreshFrameAvailability();
      this.commit();
    }

    selectFrame(frame) {
      frame = norm(frame);
      if (!this.state.format) return;
      this.state.frame = frame;
      // If the current size isn't offered in this frame, snap to an available one.
      if (this.state.size && !this.comboExists(this.state.format, this.state.size, frame)) {
        const sizes = this.sizesByFormat[this.state.format] || [];
        const altSize = sizes.find((s) => this.isComboAvailable(this.state.format, s, frame))
          || sizes.find((s) => this.comboExists(this.state.format, s, frame));
        if (altSize) {
          this.state.size = altSize;
          this.markSelectedSize(altSize);
        }
      }
      this.refreshSizeAvailability();
      // Move the selected/active swatch to the clicked frame. The
      // `.wap-swatch--selected` visual is applied inside refreshFrameAvailability
      // (it toggles `active = frame === this.state.frame`); without this call the
      // underlying variant + event update but the swatch stays on the default.
      this.refreshFrameAvailability();
      this.commit();
    }

    // ─── Rendering ────────────────────────────────────────────────────────────

    renderSizeOptions(sizes) {
      if (!this.sizeSelect) return;
      this.sizeSelect.innerHTML = '';
      for (const size of sizes) {
        const opt = document.createElement('option');
        opt.value = size; // native option label (matches variant.option2)
        opt.textContent = size;
        this.sizeSelect.appendChild(opt);
      }
    }

    markSelectedSize(size) {
      if (!this.sizeSelect) return;
      if (this.sizeSelect.value !== size) this.sizeSelect.value = size;
    }

    /** Disable size options with no existing/available variant for the current frame. */
    refreshSizeAvailability() {
      if (!this.sizeSelect || !this.state.format) return;
      for (const opt of this.sizeSelect.options) {
        const size = opt.value;
        const exists = this.comboExists(this.state.format, size, this.state.frame);
        const avail = this.isComboAvailable(this.state.format, size, this.state.frame);
        opt.disabled = !exists || !avail;
      }
    }

    renderFrameSwatches(frameLabels) {
      if (!this.frameSwatchesHost) return;
      const backgrounds = {
        siyah: '#1a1a1a',
        beyaz: '#f5f5f5',
        ahsap: 'linear-gradient(135deg, #d4ad7e 0%, #c19a6b 50%, #a87f56 100%)',
      };
      this.frameSwatchesHost.innerHTML = frameLabels
        .filter((f) => frameKeyFor(f) !== 'yok')
        .map((label) => {
          const key = frameKeyFor(label);
          const bg = backgrounds[key] || '#999';
          return `
            <button
              type="button"
              class="wap-swatch"
              data-wap-frame-label="${label}"
              aria-label="${label}"
              title="${label}"
            >
              <span class="visually-hidden">${label}</span>
              <span class="wap-swatch__color" style="background: ${bg};"></span>
            </button>
          `;
        })
        .join('');
    }

    /** Disable frame swatches whose (current size) combo doesn't exist / sold out. */
    refreshFrameAvailability() {
      if (!this.frameSwatchesHost || !this.state.format) return;
      for (const btn of this.frameSwatchesHost.querySelectorAll('[data-wap-frame-label]')) {
        const frame = btn.dataset.wapFrameLabel;
        const active = frame === this.state.frame;
        btn.classList.toggle('wap-swatch--selected', active);
        if (this.state.size) {
          const exists = this.comboExists(this.state.format, this.state.size, frame);
          const avail = this.isComboAvailable(this.state.format, this.state.size, frame);
          btn.disabled = !exists || !avail;
          btn.setAttribute('aria-disabled', btn.disabled ? 'true' : 'false');
        } else {
          btn.disabled = false;
        }
      }
      if (this.frameSelectedLabel) {
        this.frameSelectedLabel.textContent =
          (this.framePicker && !this.framePicker.hidden) ? (this.state.frame || '') : '';
      }
    }

    // ─── Commit to form + price + event ───────────────────────────────────────

    commit() {
      const { format, size, frame } = this.state;
      if (!format || !size || !frame) return;

      const variant = this.variantByCombo[this.comboKey(format, size, frame)];
      if (!variant) {
        console.warn('[wall-art-picker] no native variant for', { format, size, frame });
        return;
      }

      // Write the resolved native variant id into the product form.
      if (this.form) {
        const idInput = this.form.querySelector('[name="id"]');
        if (idInput) {
          idInput.value = String(variant.id);
          // The server-rendered hidden id can be `disabled` when the default
          // variant was sold out; re-enable so the resolved (available) variant
          // submits. The ATC button enable/disable is handled below.
          if (variant.available) idInput.removeAttribute('disabled');
          else idInput.setAttribute('disabled', 'disabled');
        }
      }

      // Reflect availability on the visible ATC (price-atc CTA + native submit).
      this.reflectAvailability(variant);

      // priceMajor = TRY major units; variant.price is integer cents.
      const priceMajor = Number(variant.price) / 100;

      // Fallback price text update for any standalone .price elements. The
      // primary visible price (wall-art-price-atc) + sale strike update from the
      // event below, so this is belt-and-suspenders for non-lite layouts.
      this.updateDisplayedPrice(priceMajor);

      const detail = {
        formatKey: formatKeyFor(format),
        frameKey: frameKeyFor(frame),
        sizeKey: sizeKeyFor(size),
        variantId: variant.id,
        price: priceMajor,
      };
      window.__wallArtCurrentSelection = detail;
      this.dispatchEvent(new CustomEvent('wallart:selection:changed', {
        bubbles: true,
        detail,
      }));
    }

    /** Enable/disable the visible ATC controls to match the resolved variant. */
    reflectAvailability(variant) {
      const available = !!variant.available;
      // price-atc CTA + its scroll-mirror clone.
      document.querySelectorAll('[data-wallart-atc-cta]').forEach((cta) => {
        if (available) {
          cta.removeAttribute('disabled');
          cta.removeAttribute('aria-disabled');
        } else {
          cta.setAttribute('disabled', 'disabled');
          cta.setAttribute('aria-disabled', 'true');
        }
      });
      // Native in-form submit (kept; usually hidden by price-atc CSS but real).
      if (this.form) {
        const submit = this.form.querySelector('.product-form__submit');
        if (submit) submit.disabled = !available;
      }
    }

    updateDisplayedPrice(priceMajor) {
      const formatted = this.formatMoney(priceMajor);
      const targets = document.querySelectorAll([
        '.price__regular .price-item--regular',
        '.price-item.price-item--regular',
        '.price__sale .price-item--regular',
        '.price-item.price-item--sale',
      ].join(','));
      for (const t of targets) t.textContent = formatted;
    }

    formatMoney(amount) {
      const fmt = this.dataset.moneyFormat || '${{amount}}';
      const fixed = amount.toFixed(2);
      const noDecimals = Math.round(amount).toString();
      return fmt
        .replace('{{amount}}', fixed)
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount_with_comma_separator}}', fixed.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals);
    }
  }

  customElements.define('wall-art-picker', WallArtPicker);
}
